import { useQuery } from "urql"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { DestinationQuery, DeliveriesQuery } from "@/lib/graphql/queries"
import type { ResultOf } from "@/lib/graphql/types"

type DeliveryItem = ResultOf<typeof DeliveriesQuery>["deliveries"]["nodes"][number]

const deliveryColumns: Column<DeliveryItem>[] = [
  {
    header: "Event",
    cell: (row) => <code className="text-xs font-mono">{row.event.eventType}</code>,
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Attempts",
    cell: (row) => (
      <span className="text-xs text-muted-foreground">
        {row.attempts}/{row.maxAttempts}
      </span>
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

export function ShareDestination({ id }: { id: string }) {
  const [result] = useQuery({ query: DestinationQuery, variables: { id } })
  const [deliveries] = useQuery({
    query: DeliveriesQuery,
    variables: { destinationId: id, limit: 20, offset: 0 },
  })

  const dest = result.data?.destination

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!dest) {
    return <EmptyState title="Destination not found" />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">{dest.name}</h1>
        <StatusBadge status={dest.status} />
      </div>

      <Card className="border-border/30">
        <CardHeader>
          <CardTitle className={sectionTitleClass}>Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel className="mb-1">URL</FieldLabel>
              <code className="text-xs font-mono">{dest.url}</code>
            </div>
            <div>
              <FieldLabel className="mb-1">Timeout</FieldLabel>
              <div>{dest.timeoutMs}ms</div>
            </div>
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={dest.createdAt} />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h2 className="text-sm font-medium">Recent Deliveries</h2>
        <DataTable
          columns={deliveryColumns}
          data={deliveries.data?.deliveries.nodes ?? []}
          keyFn={(row) => row.id}
          loading={deliveries.fetching}
          emptyTitle="No deliveries"
        />
      </div>
    </div>
  )
}
